import { Box, IconButton, styled, useTheme, Button } from '@mui/material';
import { FC, useCallback, useRef, useState } from 'react';
import { ReactComponent as ChevronRightIcon } from '../../assets/icons/chevron-right.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar.svg';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { Typography } from '../Typography/Typography';

interface MultiSelectMonthPickerProps {
  selectedYear?: number;
  selectedMonths?: number[];
  onYearChange: (year: number) => void;
  onMonthsChange: (months: number[]) => void;
  placeholder?: string;
  shouldDisableFuture?: boolean;
}

interface MonthPickerDropdownProps {
  selectedYear?: number;
  selectedMonths?: number[];
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  onYearChange: (year: number) => void;
  onMonthsChange: (months: number[]) => void;
  onClose: () => void;
  shouldDisableFuture?: boolean;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

const TriggerWrapper = styled(Box)`
  position: relative;
  display: inline-block;
`;

const DropdownWrapper = styled(Box)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  background-color: ${({ theme }) => theme.colors.surfaceBackground.bg1};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  width: 320px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
`;

const Header = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px 8px;
`;

const MonthsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 0 16px 16px;
`;

const MonthPickerDropdown: FC<MonthPickerDropdownProps> = ({
  selectedYear = new Date().getFullYear(),
  selectedMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  onYearChange,
  onMonthsChange,
  shouldDisableFuture = true,
}) => {
  const { colors } = useTheme();
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const isNextYearDisabled = shouldDisableFuture && selectedYear >= currentYear;

  const handlePreviousYear = useCallback(() => {
    onYearChange(selectedYear - 1);
  }, [selectedYear, onYearChange]);

  const handleNextYear = useCallback(() => {
    if (isNextYearDisabled) return;
    onYearChange(selectedYear + 1);
  }, [selectedYear, onYearChange, isNextYearDisabled]);

  const isMonthDisabled = useCallback(
    (monthIndex: number) => {
      if (!shouldDisableFuture) return false;
      return (
        selectedYear > currentYear || (selectedYear === currentYear && monthIndex >= currentMonth)
      );
    },
    [shouldDisableFuture, selectedYear, currentYear, currentMonth]
  );

  const handleMonthClick = useCallback(
    (monthIndex: number) => {
      if (isMonthDisabled(monthIndex)) return;

      const isSelected = selectedMonths.includes(monthIndex);
      if (isSelected) {
        onMonthsChange(selectedMonths.filter((month) => month !== monthIndex));
      } else {
        onMonthsChange([...selectedMonths, monthIndex]);
      }
    },
    [selectedMonths, onMonthsChange, isMonthDisabled]
  );

  const getAvailableMonths = useCallback(() => {
    if (!shouldDisableFuture) return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    if (selectedYear > currentYear) return [];
    if (selectedYear < currentYear) return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    // Current year - only include months in the past (before current month)
    return Array.from({ length: currentMonth }, (_, i) => i);
  }, [shouldDisableFuture, selectedYear, currentYear, currentMonth]);

  const handleSelectAll = useCallback(() => {
    const availableMonths = getAvailableMonths();
    const selectedAvailableMonths = selectedMonths.filter((month) =>
      availableMonths.includes(month)
    );

    if (selectedAvailableMonths.length === availableMonths.length) {
      // Deselect all available months
      onMonthsChange(selectedMonths.filter((month) => !availableMonths.includes(month)));
    } else {
      // Select all available months
      const allSelected = [...new Set([...selectedMonths, ...availableMonths])];
      onMonthsChange(allSelected);
    }
  }, [selectedMonths, onMonthsChange, getAvailableMonths]);

  return (
    <DropdownWrapper>
      <Header>
        <IconButton onClick={handlePreviousYear} size='small'>
          <ChevronRightIcon
            style={{
              rotate: '180deg',
            }}
          />
        </IconButton>

        <Typography variant='body' color={colors.text.main}>
          {selectedYear}
        </Typography>

        <IconButton
          onClick={handleNextYear}
          size='small'
          disabled={isNextYearDisabled}
          style={{
            opacity: isNextYearDisabled ? 0.5 : 1,
            cursor: isNextYearDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Header>

      <Typography
        variant='caption'
        color={colors.text.caption}
        style={{
          borderBottom: `1px solid ${colors.border.default}`,
          paddingLeft: '16px',
          paddingBottom: '8px',
        }}
      >
        Select one or multiple months
      </Typography>

      <Button
        variant='outlined'
        onClick={handleSelectAll}
        style={{
          backgroundColor: colors.surfaceBackground.bg1,
          margin: '8px 16px',
        }}
      >
        {selectedMonths.length === 12 ? 'Deselect All' : 'Select All'}
      </Button>

      <MonthsGrid>
        {MONTHS.map((month, index) => {
          const disabled = isMonthDisabled(index);
          const selected = selectedMonths.includes(index);
          return (
            <Button
              variant='text'
              key={month}
              onClick={() => handleMonthClick(index)}
              disabled={disabled}
              style={{
                backgroundColor:
                  selected && !disabled ? colors.surfaceAccent.default : 'transparent',
                borderRadius: '20px',
                transition: 'all 0.2s ease',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <Typography
                variant='body'
                color={selected ? '#FFFFFF' : disabled ? colors.text.caption : colors.text.main}
              >
                {month}
              </Typography>
            </Button>
          );
        })}
      </MonthsGrid>
    </DropdownWrapper>
  );
};

export const MultiSelectMonthPicker: FC<MultiSelectMonthPickerProps> = ({
  selectedYear = new Date().getFullYear(),
  selectedMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  onYearChange,
  onMonthsChange,
  placeholder = 'Select months',
  shouldDisableFuture = true,
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const boxRef = useRef(null);

  useOutsideClick(boxRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const getDisplayText = useCallback(() => {
    if (selectedMonths?.length === 0) {
      return placeholder;
    }
    if (selectedMonths?.length === 12) {
      return `All months ${selectedYear}`;
    }
    if (selectedMonths?.length === 1) {
      return `${MONTHS[selectedMonths[0]]} ${selectedYear}`;
    }
    return `${selectedMonths?.length} months, ${selectedYear}`;
  }, [selectedMonths, selectedYear, placeholder]);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <TriggerWrapper ref={boxRef}>
      <IconButton onClick={handleToggle}>
        <Typography
          variant='body'
          color={colors.text.main}
          style={{
            fontSize: '18px',
            marginRight: '8px',
          }}
        >
          {getDisplayText()}
        </Typography>
        <Box
          sx={{
            svg: { path: { fill: colors.icon.accent } },
            border: `1px solid ${colors.border.default}`,
            borderRadius: '4px',
            width: '36px',
            height: '36px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CalendarIcon style={{ transform: 'scale(1.5)' }} />
        </Box>
      </IconButton>

      {isOpen && (
        <MonthPickerDropdown
          selectedYear={selectedYear}
          selectedMonths={selectedMonths}
          onYearChange={onYearChange}
          onMonthsChange={onMonthsChange}
          onClose={handleClose}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          shouldDisableFuture={shouldDisableFuture}
        />
      )}
    </TriggerWrapper>
  );
};
